import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { JwtHelperService } from '@auth0/angular-jwt'

import {
  AUTH_STORE_KEY,
  getHttpHeaders,
  USER_ROLE_STORE_KEY,
} from '../constants/constants'
import { environment } from '@env'
import { decryptData, encryptData } from '@/app/helpers/crypto.util'

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  public redirectUrl: string | undefined
  public logoutMessage: string | undefined
  public hasSessionExpired: boolean = false
  private jwtHelper = new JwtHelperService()
  public userRole!: string

  constructor(
    private router: Router,
    private http: HttpClient
  ) {}

  renewToken(): Observable<any> {
    return of(null)
  }

  revokeToken(): Observable<any> {
    return this.http.put(
      `${environment.BASE_URL}/v1/user/signout`,
      {},
      {
        headers: getHttpHeaders(),
        observe: 'response',
      }
    )
  }

  isLoggedIn(): boolean {
    const authInfo = this.getAuthInfo()

    if (!authInfo || !authInfo.access_token) {
      return false
    }

    const isExpired = this.jwtHelper.isTokenExpired(authInfo.access_token)

    if (isExpired) {
      this.hasSessionExpired = true
      return false
    }

    return true
  }

  storeAuthData(
    access_token: string,
    refresh_token: string,
    token_type: string = 'Bearer',
    userData?: any
  ): void {
    const authData = {
      access_token,
      refresh_token,
      token_type,
      user: userData,
      timestamp: new Date().getTime(),
    }

    try {
      const encrypted = encryptData(authData)

      localStorage.setItem(AUTH_STORE_KEY, encrypted)
    } catch (error) {
      console.error('Failed to encrypt data:', error)
      localStorage.setItem(AUTH_STORE_KEY, JSON.stringify(authData))
    }
  }

  public getAuthInfo(): any {
    try {
      const storedData = localStorage.getItem(AUTH_STORE_KEY)

      if (!storedData) return null

      if (storedData.startsWith('{') && storedData.endsWith('}')) {
        return JSON.parse(storedData)
      }

      return decryptData(storedData)
    } catch (error) {
      console.error('Error retrieving auth info:', error)
      return null
    }
  }

  public getAccessToken(): string | null {
    const authInfo = this.getAuthInfo()
    return authInfo?.access_token || null
  }

  public getTokenType(): string | null {
    const authInfo = this.getAuthInfo()
    return authInfo?.token_type || 'Bearer'
  }

  logout() {
    this.revokeToken().subscribe({
      next: (res) => {
        if (res.body?.code === 200) {
          this.logoutV2()
        }
      },
      error: (err) => {
        this.logoutV2()
      },
      complete: () => {},
    })
  }

  logoutV2() {
    localStorage.removeItem(AUTH_STORE_KEY)
    this.logoutNavigate()
  }

  logoutNavigate() {
    if (this.hasSessionExpired) {
      this.router.navigate(['/session-expired'])
    } else {
      this.router.navigate(['/auth/sign-in'])
    }
  }

  validateExistingSession(): boolean {
    const authInfo = this.getAuthInfo()
    if (!authInfo || !authInfo.access_token) {
      return false
    }

    const isExpired = this.jwtHelper.isTokenExpired(authInfo.access_token)

    if (isExpired) {
      this.hasSessionExpired = true
      localStorage.removeItem(AUTH_STORE_KEY)
      return false
    }

    return true
  }

  public getUserInfo(): any {
    const authInfo = this.getAuthInfo()
    return authInfo?.user || null
  }

  public hasRole(role: string): boolean {
    const userInfo = this.getUserInfo()
    return userInfo?.role === role
  }

  public setUserRole(role: string): void {
    try {
      const encrypted = encryptData(role)
      localStorage.setItem(USER_ROLE_STORE_KEY, encrypted)
    } catch (error) {
      console.error('Error encrypting role:', error)
    }
  }

  public getUserRole(): string | null {
    try {
      const stored = localStorage.getItem(USER_ROLE_STORE_KEY)
      if (!stored) return null
      return decryptData(stored)
    } catch (error) {
      console.error('Error decrypting role:', error)
      return null
    }
  }
}
